/**
 * NOS-54: Public blog post detail at /blog/:postSlug or /:orgSlug/blog/:postSlug.
 * Renders post body via BlockRenderer, share buttons, related posts.
 */

import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { PublicSitePage } from "@/components/PublicSitePage"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { resolveBlogPostHead } from "@/lib/seo/headData"
import { blogPostingJsonLd } from "@/lib/seo/structuredData"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import { BlockRenderer } from "@/components/website-builder/BlockRenderer"
import { BlogPostHeader } from "@/components/blog/BlogPostHeader"
import { calcReadingTime, formatPostDate, splitLeadingHero } from "@/lib/blog/postMeta"
import type { Organization, OrgCustomizations, PageSection } from "@/templates/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowLeft, FileText, Copy, Check } from "lucide-react"
import { toast } from "sonner"

interface BlogPostRow {
  id: string
  org_id: string
  title: string
  slug: string
  excerpt: string | null
  cover_image: string | null
  content_sections: unknown
  category_id: string | null
  author_id: string | null
  published_at: string | null
  updated_at: string | null
  /** NOS-249: per-post SEO overrides, editable in the blog editor */
  seo_title?: string | null
  seo_description?: string | null
  og_image?: string | null
}

interface BlogPostPageProps {
  orgSlug: string
  postSlug: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  basePath?: string
}

function contentToSections(content: unknown, orgId: string): PageSection[] {
  const arr = Array.isArray(content) ? content : []
  return (arr as Record<string, unknown>[]).map((item, i) => ({
    id: String(item.id ?? crypto.randomUUID()),
    section_type: String(item.section_type ?? "paragraph"),
    order_index: typeof item.order_index === "number" ? item.order_index : i,
    content: (item.content as Record<string, unknown>) ?? null,
    page_id: "",
    org_id: orgId,
    is_visible: true,
  }))
}

export function BlogPostPage({
  orgSlug,
  postSlug,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: BlogPostPageProps) {
  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org, cust, primaryColor } = shell

  const [post, setPost] = useState<BlogPostRow | null>(null)
  const [category, setCategory] = useState<{ name: string; slug: string; color: string | null } | null>(null)
  const [relatedPosts, setRelatedPosts] = useState<BlogPostRow[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [copied, setCopied] = useState(false)

  const orgId = org?.id
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!postSlug) {
        setLoading(false)
        setNotFound(true)
        return
      }
      if (!orgId) return

      const { data: postData, error: postErr } = await supabase
        .from("blog_posts")
        .select("id, org_id, title, slug, excerpt, cover_image, content_sections, category_id, author_id, published_at, updated_at, seo_title, seo_description, og_image")
        .eq("org_id", orgId)
        .eq("slug", postSlug)
        .eq("is_published", true)
        .not("published_at", "is", null)
        .lte("published_at", new Date().toISOString())
        .maybeSingle()

      if (cancelled) return
      if (postErr || !postData) {
        setNotFound(true)
        setLoading(false)
        return
      }

      const postRow = postData as BlogPostRow
      setPost(postRow)

      if (postRow.category_id) {
        const { data: catData } = await supabase
          .from("blog_categories")
          .select("name, slug, color")
          .eq("id", postRow.category_id)
          .maybeSingle()
        if (!cancelled && catData) setCategory(catData as { name: string; slug: string; color: string | null })
      }

      let relatedQuery = supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, cover_image, published_at")
        .eq("org_id", orgId)
        .eq("is_published", true)
        .not("published_at", "is", null)
        .lte("published_at", new Date().toISOString())
        .neq("id", postRow.id)
        .order("published_at", { ascending: false })
        .limit(3)

      if (postRow.category_id) {
        relatedQuery = relatedQuery.eq("category_id", postRow.category_id)
      }

      const { data: relatedData } = await relatedQuery
      if (!cancelled && relatedData) setRelatedPosts(relatedData as BlogPostRow[])

      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [orgId, postSlug])

  const sectionBlocks = useMemo(() => {
    if (!post) return []
    return contentToSections(post.content_sections, post.org_id)
  }, [post])

  // NOS-220: a leading hero block's presentation is the shared header, so it
  // is dropped from the body (prevents a duplicate title under the header).
  const bodyBlocks = useMemo(() => splitLeadingHero(sectionBlocks).bodyBlocks, [sectionBlocks])

  // NOS-249: title/description/canonical/og:image resolution moved into the
  // shared renderer-agnostic resolver (explicit override → excerpt/cover →
  // generated branded default).
  const headData = useMemo(() => {
    if (!post || !org) return null
    return resolveBlogPostHead({ org: org, post, sections: sectionBlocks })
  }, [post, org, sectionBlocks])

  // NOS-242: regenerated BlogPosting JSON-LD via the shared generator
  // (adds url/mainEntityOfPage + publisher logo; drops empty dates).
  const articleJsonLd = useMemo(() => {
    if (!post || !org || !headData) return null
    return blogPostingJsonLd({
      title: post.title,
      url: headData.canonicalUrl,
      description: headData.description,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      imageUrl: headData.ogImage,
      orgName: org.name,
      orgUrl: getPublicSiteOrigin(org),
      orgLogoUrl: org.logo_url,
    })
  }, [post, org, headData])

  const handleShare = (type: string) => {
    const url = typeof window !== "undefined" ? window.location.href : ""
    const text = post?.title ?? ""
    if (type === "copy") {
      navigator.clipboard.writeText(url)
      setCopied(true)
      toast.success("Link copied")
      setTimeout(() => setCopied(false), 2000)
      return
    }
    const urls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    }
    if (urls[type]) window.open(urls[type], "_blank", "width=600,height=400")
  }

  if (loading || shell.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (notFound || shell.notFound || !post || !org) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold">404</h1>
        <p className="text-muted-foreground">Post not found.</p>
        <Button asChild variant="outline">
          <Link to={`${basePath}/blog`}>Back to Blog</Link>
        </Button>
      </div>
    )
  }

  return (
    <PublicSitePage
      shell={shell}
      head={
        headData ? (
          <SEOHead
            title={headData.title}
            description={headData.description}
            canonicalUrl={headData.canonicalUrl}
            ogType={headData.ogType}
            imageUrl={headData.ogImage}
            jsonLd={articleJsonLd}
          />
        ) : null
      }
    >
      {/* NOS-220: shared post header — full-bleed hero when a cover image
          exists, clean header otherwise. A leading hero block (builder-created
          posts) is replaced by this header, matching the canvas. */}
      <BlogPostHeader
        title={post.title}
        coverImage={post.cover_image}
        categoryName={category?.name ?? null}
        categoryColor={category?.color ?? null}
        categoryLinkTo={category ? `${basePath}/blog?category=${category.slug}` : undefined}
        publishedAt={post.published_at}
        updatedAt={post.updated_at}
        readingTimeMinutes={calcReadingTime(sectionBlocks)}
        primaryColor={primaryColor}
      />

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`${basePath}/blog`} className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Link>
        </Button>

        {/* NOS-221: no `prose` wrapper — now that @tailwindcss/typography is
            installed it would restyle every section block (buttons, images,
            headings); ParagraphBlock wraps its own body copy in `prose`. */}
        <div className="max-w-none">
          {bodyBlocks.map((section) => (
            <BlockRenderer
              key={section.id}
              section={section}
              customizations={cust}
              org={org}
            />
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          <span className="flex items-center text-sm text-muted-foreground">Share:</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleShare("copy")}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleShare("twitter")}>
            X
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleShare("facebook")}>
            f
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleShare("linkedin")}>
            in
          </Button>
        </div>

        {relatedPosts.length > 0 && (
          <section className="mt-16">
            <h2 className="text-xl font-semibold">Related posts</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {relatedPosts.map((p) => (
                <Link key={p.id} to={`${basePath}/blog/${p.slug}`}>
                  <Card className="h-full overflow-hidden transition hover:shadow-md">
                    <div className="aspect-video bg-muted">
                      {p.cover_image ? (
                        <img src={p.cover_image} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div
                          className="flex h-full items-center justify-center"
                          style={{ backgroundColor: `${primaryColor}20` }}
                        >
                          <FileText className="h-8 w-8 opacity-50" style={{ color: primaryColor }} />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-medium line-clamp-2">{p.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatPostDate(p.published_at)}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </PublicSitePage>
  )
}
